import type { ReactNode } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PropostaPublicaHeaderStatus } from '@/components/PropostaPublica/PropostaPublicaHeaderStatus'
import type { PropostaValidityInfo } from '@/utils/propostaPublicaUtils'
import type { WizardStep } from '@/utils/propostaPublicaUtils'

type PropostaPublicaShellProps = {
  validity: PropostaValidityInfo
  step: WizardStep
  showNav: boolean
  canGoNext: boolean
  accepting?: boolean
  onBack: () => void
  onNext: () => void
  onAccept?: () => void
  children: ReactNode
  footerExtra?: ReactNode
}

export function PropostaPublicaShell({
  validity,
  step,
  showNav,
  canGoNext,
  accepting = false,
  onBack,
  onNext,
  onAccept,
  children,
  footerExtra,
}: PropostaPublicaShellProps) {
  return (
    <div className="proposta-publica-page min-h-screen flex flex-col bg-[#f7f7f8] text-[#0c0d0e]">
      <header className="sticky top-0 z-20 border-b border-black/[0.06] bg-white/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <img
              src="/logo-rocket-light.svg"
              alt="Rocket Search"
              className="h-9 md:h-11 w-auto shrink-0 object-contain"
            />
            <span className="hidden sm:block h-6 w-px bg-black/10" aria-hidden />
            <p className="hidden sm:block text-[11px] font-semibold uppercase tracking-[0.18em] text-[#52525b]">
              Proposta comercial
            </p>
          </div>
          <PropostaPublicaHeaderStatus validity={validity} />
        </div>
      </header>

      <main className="relative z-10 flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {children}
      </main>

      {showNav && step > 1 && (
        <footer className="sticky bottom-0 z-20 border-t border-black/[0.06] bg-white/95 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button variant="secondary" size="sm" icon={<ArrowLeft size={14} />} onClick={onBack}>
                Voltar
              </Button>
            ) : (
              <div />
            )}
            {step === 2 && onAccept ? (
              <Button
                variant="primary"
                size="sm"
                disabled={!canGoNext}
                loading={accepting}
                onClick={onAccept}
              >
                Aceitar Proposta
              </Button>
            ) : step < 3 ? (
              <Button
                variant="primary"
                size="sm"
                icon={<ArrowRight size={14} />}
                disabled={!canGoNext}
                onClick={onNext}
              >
                Continuar
              </Button>
            ) : (
              <div />
            )}
          </div>
        </footer>
      )}

      {footerExtra}
    </div>
  )
}
